/**
 * GitLab instance URL helpers.
 *
 * The configured GitLab host of a runner is a free-form URL entered by the user.
 * These helpers canonicalize it so that equivalent spellings (trailing slash,
 * hostname casing, explicit default ports, query/hash fragments) count as the
 * same GitLab instance, while different schemes, non-default ports or
 * meaningful subpaths stay distinct.
 */

/**
 * Canonicalize a configured GitLab host for instance identity, display and linking.
 *
 * Returns a URL string without query/hash and without a trailing root slash,
 * e.g. "https://GITLAB.example.com/" -> "https://gitlab.example.com".
 * Returns null for blank, malformed or non-HTTP(S) values.
 */
export function normalizeGitlabInstance(host: string | null | undefined): string | null {
  const raw = (host ?? '').trim();
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  // url.origin canonicalizes protocol/hostname casing and drops default ports.
  // Keep the path casing: subpaths may be case-sensitive.
  const path = url.pathname.replace(/\/+$/, '');
  return url.origin + path;
}

/**
 * Number of distinct GitLab instances across the given hosts.
 * Blank or malformed hosts are ignored.
 */
export function countDistinctGitlabInstances(hosts: (string | null | undefined)[]): number {
  const distinct = new Set<string>();
  for (const host of hosts) {
    const normalized = normalizeGitlabInstance(host);
    if (normalized) distinct.add(normalized);
  }
  return distinct.size;
}
